import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePlatformSettings } from "@/hooks/usePlatformSettings";

/**
 * Global ACEX Card status. While `live` is false, the public /cards page and the
 * in-app Virtual Cards surfaces render a locked "Coming Soon" state with a
 * notify-me signup, and the virtual-cards edge function rejects issuing/top-up.
 */
export function useCardsStatus() {
  const { data, isLoading } = useQuery({
    queryKey: ["platform_settings", "cards"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_public_platform_settings");
      if (error) throw error;
      return data as any;
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });

  const live = (data?.cards_live as boolean) ?? false;
  return { live, locked: !live, loading: isLoading, settingsId: data?.id as string | undefined };
}

export function useSetCardsLive() {
  const qc = useQueryClient();
  const { settingsId } = usePlatformSettings();

  return useMutation({
    mutationFn: async (live: boolean) => {
      if (!settingsId) throw new Error("Platform settings not initialised");
      const { error } = await supabase
        .from("platform_settings" as any)
        .update({ cards_live: live } as any)
        .eq("id", settingsId);
      if (error) throw error;
      return live;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["platform_settings"] });
    },
  });
}
